import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceIcon from '@mui/icons-material/Place';

// Note: For simplicity in this example, we'll create a map-like placeholder
// In a real-world implementation, you'd integrate with Google Maps or another mapping service
function MapPicker({ latitude, longitude, onChange }) {
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState({
    lat: latitude || 38.9622,
    lng: longitude || -76.9348
  });

  // Update the location state when props change
  useEffect(() => {
    if (latitude && longitude) {
      setLocation({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude)
      });
    }
  }, [latitude, longitude]);

  // Simulate geocoding (in real app, would use Google Maps Geocoding API)
  const handleAddressSearch = () => {
    if (!address.trim()) return;
    
    setLoading(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      // Generate slightly random coordinates based on current location
      // (This is just for demo - a real app would use proper geocoding)
      const newLat = location.lat + (Math.random() * 0.01 - 0.005);
      const newLng = location.lng + (Math.random() * 0.01 - 0.005);
      
      setLocation({
        lat: newLat,
        lng: newLng
      });
      
      onChange(newLat, newLng);
      setLoading(false);
    }, 1000);
  };

  // Simulate getting user location
  const handleGetCurrentLocation = () => {
    setLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({
            lat: latitude,
            lng: longitude
          });
          onChange(latitude, longitude);
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLoading(false);
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
      setLoading(false);
    }
  };

  // Handle manual marker drag (simulated in this placeholder)
  const handleMapClick = (e) => {
    // Get click coordinates relative to the map container
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; // x position within the element
    const y = e.clientY - rect.top;  // y position within the element
    
    // Convert to latitude/longitude (very simplified approximation)
    // This is just for demonstration - real apps would use proper mapping APIs
    const mapWidth = rect.width;
    const mapHeight = rect.height;
    
    // Calculate lat/lng based on relative position in the map container
    // (This is just a visual approximation for the demo)
    const latDiff = 0.01; // Approximate degrees to represent map height
    const lngDiff = 0.02; // Approximate degrees to represent map width
    
    const newLat = location.lat + (latDiff/2) - (y / mapHeight * latDiff);
    const newLng = location.lng - (lngDiff/2) + (x / mapWidth * lngDiff);
    
    setLocation({
      lat: newLat,
      lng: newLng
    });
    
    onChange(newLat, newLng);
  };

  return (
    <Box sx={{ mb: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          label="Search address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleAddressSearch();
          }}
        />
        <Button 
          variant="contained" 
          onClick={handleAddressSearch}
          disabled={loading || !address.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={handleGetCurrentLocation}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <MyLocationIcon />}
        >
          Current
        </Button>
      </Box>
      
      {/* Map placeholder - in a real app, use actual map component */}
      <Box
        sx={{
          width: '100%',
          height: 300,
          bgcolor: '#e5e5e5',
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'crosshair',
          backgroundImage: 'url("https://firebasestorage.googleapis.com/v0/b/b-b-q-finder-qcoftk.appspot.com/o/map-placeholder.png?alt=media")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onClick={handleMapClick}
      >
        {/* Centered marker */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -100%)',
            color: 'error.main',
            zIndex: 10,
          }}
        >
          <PlaceIcon sx={{ fontSize: 40 }} />
        </Box>
        
        {/* Coordinates display */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            p: 1,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">
            Lat: {location.lat.toFixed(6)}° {location.lat > 0 ? 'N' : 'S'}, 
            Lng: {location.lng.toFixed(6)}° {location.lng > 0 ? 'E' : 'W'}
          </Typography>
        </Box>
      </Box>
      
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
        Click on the map to set location or use the search/current location buttons
      </Typography>
    </Box>
  );
}

export default MapPicker;